/**
 * VentureKhojLogo — canonical two-tone brand mark.
 *   Icon: custom VK monogram with emerald compass arrow (see /public/brand/vk-icon-*.svg)
 *   Wordmark: "Venture" in navy/white + "Khoj" in emerald
 * Use `variant="light"` on dark backgrounds; `variant="dark"` on light.
 * Sizes: nav | md | lg
 * `showTagline` renders "DISCOVER YOUR NEW VENTURE" underneath.
 */
export function VentureKhojLogo({
  variant = "dark",
  size = "md",
  showTagline = true,
  className = "",
}) {
  const iconPx =
    size === "lg" ? 56 : size === "nav" ? 48 : 40;
  const wordmark =
    size === "lg"
      ? "text-3xl sm:text-4xl font-black tracking-tight"
      : size === "nav"
      ? "text-xl sm:text-2xl font-black tracking-tight"
      : "text-lg sm:text-xl font-black tracking-tight";
  const tag =
    size === "lg" ? "text-[10px]" : "text-[9px] sm:text-[9.5px]";

  const ventureColor = variant === "light" ? "text-white" : "text-navy";
  const khojColor =
    variant === "light" ? "text-emerald-400" : "text-emerald-600";
  // Tagline: solid black on light surfaces, near-black on dark surfaces so it
  // sits under the wordmark as a quiet secondary line — matches user brief.
  const tagColor =
    variant === "light" ? "text-slate-900" : "text-slate-900";

  // On dark bg we use the emerald-tile icon so it pops against navy;
  // on light bg we use the navy-tile icon so the mark reads clearly.
  const iconSrc =
    variant === "light" ? "/brand/vk-icon-emerald.svg" : "/brand/vk-icon-navy.svg";

  return (
    <div className={"flex items-center gap-3 min-w-0 " + className} data-testid="brand-logo">
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        width={iconPx}
        height={iconPx}
        className="shrink-0"
        style={{ width: iconPx, height: iconPx }}
      />
      <div className="leading-none min-w-0 inline-block">
        <div className={"font-heading " + wordmark + " truncate"}>
          <span className={ventureColor}>Venture</span>
          <span className={khojColor}>Khoj</span>
          <sup className={khojColor + " text-[0.68em] font-extrabold align-super tracking-normal ml-px"}>™</sup>
        </div>
        {showTagline && (
          <div
            className={
              "-mt-0.5 uppercase font-semibold whitespace-nowrap text-center " +
              tag +
              " " +
              tagColor
            }
            style={{
              letterSpacing: "0.02em",
              textAlign: "justify",
              textAlignLast: "justify",
              display: "block",
              width: "100%",
            }}
          >
            Discover, Compare, Decide
          </div>
        )}
      </div>
    </div>
  );
}
